import styled from '@emotion/styled'
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import {
  Button,
  Empty,
  Input,
  InputNumber,
  Pagination,
  Segmented,
  Skeleton,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import StockImageThumb from 'components/ManageProduct/StockImageThumb'
import { backendUrl } from 'config'
// useToggleSkuStock ถูกถอดออก — toggle จะไม่ยิง API ทันที แต่รอกด "อัปเดต" ก่อน
import { useListProductLinkedStock } from 'hooks/useProductLinkedStock'
import { numberFormatInt } from 'utils/nuberFormat'
import { createInputNumberGuards } from 'utils'
import type { IProductStockInfo } from 'services/interface'
import { useSkuStockPicker, useCombinedStockPicker } from 'hooks/useProductStockPicker'
import { useSkuOwnStock } from 'hooks/useProduct'
import { useListProductRecipes } from 'hooks/useProductRecipe'
import type { ProductRecipeRecord } from 'services/interface/productRecipe.interface'
import type { ProductStockPickerRow } from 'services/interface/productStockPicker.interface'
import type {
  LinkedStockSourceKind,
  LinkedStockUiMode,
  SkuLinkedStockDraftRow,
} from 'services/interface/productLinkedStock.interface'
import { normalizeLinkedQuantity } from 'services/interface/productLinkedStock.interface'
import {
  SET_TAG_LABEL,
  STOCK_MODE_ADVANCED_DESC,
  STOCK_MODE_ADVANCED_LABEL,
  STOCK_MODE_BASIC_DESC,
  STOCK_MODE_BASIC_LABEL,
} from 'components/ManageProduct/Recipe/bundleLabels'
import { useSkuModalStockEdit } from './SkuModalStockEditContext'
import {
  ENABLE_ADVANCED_SELF_STOCK,
  SKU_MODAL_WAREHOUSE_WIDTH,
} from './skuModalConstants'

/** ขนาดต่อหน้าของตาราง "เพิ่มรายการจาก" — ดึงข้อมูลจาก server ทีละหน้า (server-side pagination) */
const PICKER_PAGE_SIZE = 10

/** ประกอบ URL รูปจาก id (master_image) ด้วย backendUrl ฝั่งหน้าบ้าน — ทนต่อ URL_API ต่าง env */
const buildImageUrl = (id?: string | null): string | undefined =>
  id ? `${backendUrl}images/${id}` : undefined

/** รูปของ linked row: ใช้ ref_image_id ประกอบเองก่อน แล้ว fallback URL เต็มจาก backend */
const linkedRowThumbSrc = (row: {
  ref_image_id?: string | null
  ref_image_url?: string | null
}): string | undefined => buildImageUrl(row.ref_image_id) ?? row.ref_image_url ?? undefined

/**
 * ชื่อของ "สต็อกของตัวเอง" ให้ตามชื่อ SKU ในแท็บราคา
 * label จาก backend เป็น "ชื่อสินค้า — ชื่อ SKU" → เปลี่ยนเฉพาะส่วนชื่อ SKU (คงชื่อสินค้าไว้)
 */
const liveOwnStockLabel = (label: string, skuName?: string): string => {
  const name = skuName?.trim()
  if (!name) return label || '(ยังไม่ได้ตั้งชื่อ)'
  const sep = label?.lastIndexOf(' — ') ?? -1
  return sep >= 0 ? `${label.slice(0, sep)} — ${name}` : name
}

/** รูปของ picker row: ใช้ images[0] ประกอบเองก่อน แล้ว fallback image_url */
const pickerRowThumbSrc = (row: {
  images?: string[] | null
  image_url?: string | null
}): string | undefined => buildImageUrl(row.images?.[0]) ?? row.image_url ?? undefined

/**
 * แถวที่แก้สต็อกต้นทางได้ — เฉพาะ "สต็อก SKU ของตัวเอง" (ref_stock_sku_id ตรงกับ SKU ใน modal) ที่บันทึกแล้ว
 * ถ้าไม่ใช่ sku ตัวเอง (เช่นสต็อก SKU ของสินค้าอื่นที่ผูกมา / มาสเตอร์) → แก้ไขสต็อกไม่ได้
 */
const canEditLinkedStockRow = (
  row: SkuLinkedStockDraftRow,
  skuItemId: string | null | undefined
): boolean =>
  !!row.linked_stock_id &&
  row.ref_type === 'product_stock' &&
  row.source_kind === 'sku' &&
  !!skuItemId &&
  row.ref_stock_sku_id === skuItemId

type StockSummaryBlockProps = {
  stock?: IProductStockInfo | null
  loading?: boolean
  /** ใช้ layout แบบเดิม (StockSummaryRow/StockSummary) */
}

const StockSummaryBlock: FC<StockSummaryBlockProps> = ({ stock, loading }) => {
  if (loading) {
    return (
      <StockSummaryRow>
        <Skeleton.Button active size="small" shape="round" style={{ width: 72, height: 22 }} />
        <Skeleton.Button active size="small" shape="round" style={{ width: 60, height: 22 }} />
        <Skeleton.Button active size="small" shape="round" style={{ width: 80, height: 22 }} />
      </StockSummaryRow>
    )
  }
  if (!stock) return null
  return (
    <StockSummary>
      <StockStat>
        <StockStatLabel>สต็อกทั้งหมด</StockStatLabel>
        <StockStatValue $tone="default">
          {numberFormatInt(stock.stock_quantity)}
        </StockStatValue>
      </StockStat>
      <StockStatDivider aria-hidden />
      <StockStat $emphasis>
        <StockStatLabel>กำลังมีคนซื้อ</StockStatLabel>
        <StockStatValue $tone="hold">
          {numberFormatInt(stock.holding_quantity)}
        </StockStatValue>
      </StockStat>
      <StockStatDivider aria-hidden />
      <StockStat>
        <StockStatLabel>ขายได้</StockStatLabel>
        <StockStatValue
          $tone="balance"
          $positive={(stock.stock_balance ?? 0) > 0}
        >
          {numberFormatInt(stock.stock_balance)}
        </StockStatValue>
      </StockStat>
    </StockSummary>
  )
}

/** toggle สลับโหมดสต็อก กำหนดโดยตรง/คำนวณจากสต็อกหลัก
 *  ตัวที่ selected จะทำสีตามโหมด (กำหนดโดยตรง = ฟ้า, คำนวณ = ม่วง) */
const StockModeToggle: FC<{
  mode: 'basic' | 'advanced'
  onChange: (mode: 'basic' | 'advanced') => void
  disabled?: boolean
}> = ({ mode, onChange, disabled }) => (
  <ModeToggleWrap $variant={mode}>
    <Segmented
      size="small"
      value={mode}
      disabled={disabled}
      onChange={(v) => onChange(v as 'basic' | 'advanced')}
      options={[
        { value: 'basic', label: STOCK_MODE_BASIC_LABEL },
        { value: 'advanced', label: STOCK_MODE_ADVANCED_LABEL },
      ]}
    />
  </ModeToggleWrap>
)

type BasicModeViewProps = {
  draftRows: SkuLinkedStockDraftRow[]
  stock?: IProductStockInfo | null
  showStockSummary: boolean
  waitingStockRefetch: boolean
  onSwitchToAdvanced: () => void
  skuItemId?: string | null
  /** หน้าเพิ่มสินค้า: จำนวนสต็อกเริ่มต้น — มี onInitialQuantityChange = แสดงช่องกรอกแทนสรุปสต็อก */
  initialQuantity?: number
  onInitialQuantityChange?: (qty: number) => void
  /** true = สต็อกหลักถูกสร้าง/เก็บแล้ว (หน้าเพิ่มสินค้าไม่มี linked_stock_id ให้ดู) */
  mainStockSaved?: boolean
  /** มีค่า = ยังไม่มีสต็อกหลัก → แสดงปุ่ม "สร้างสต็อกหลัก" แทนป้าย "ยังไม่ได้บันทึก" */
  onCreateMainStock?: () => void
  creatingMainStock?: boolean
}

const BasicModeView: FC<BasicModeViewProps> = ({
  draftRows,
  stock,
  showStockSummary,
  waitingStockRefetch,
  onSwitchToAdvanced,
  skuItemId,
  initialQuantity = 0,
  onInitialQuantityChange,
  mainStockSaved = false,
  onCreateMainStock,
  creatingMainStock = false,
}) => {
  const { openStockEdit } = useSkuModalStockEdit()
  const firstRow = draftRows[0]
  const stockEditable = firstRow ? canEditLinkedStockRow(firstRow, skuItemId) : false
  // มี linked_stock_id = ถูกบันทึกลงระบบแล้ว, ไม่มี = ยังเป็นค่าที่รอบันทึก
  const isSaved = !!firstRow?.linked_stock_id || mainStockSaved

  return (
    <>
      <BasicModeHeader>
        <ModeDesc>{STOCK_MODE_BASIC_DESC}</ModeDesc>
        <StockModeToggle
          mode="basic"
          onChange={(m) => {
            if (m === 'advanced') onSwitchToAdvanced()
          }}
        />
      </BasicModeHeader>
      <BasicWhiteCard $variant="split">
        {/* ยังไม่มีสต็อกหลัก = ปุ่ม "สร้างสต็อกหลัก" แทนสถานะ → ไม่ต้องมีคำอธิบายสถานะ */}
        {!isSaved && onCreateMainStock ? null : (
        <BasicCardLegendRow>
          <SaveStatusLegend>
            <SaveStatusLegendItem>
              <SaveStatusDot $variant="saved" />
              <span>บันทึกแล้ว</span>
            </SaveStatusLegendItem>
            <SaveStatusLegendItem>
              <SaveStatusDot $variant="draft" />
              <span>ยังไม่ได้บันทึก</span>
            </SaveStatusLegendItem>
          </SaveStatusLegend>
        </BasicCardLegendRow>
        )}
        <BasicModeLayout>
          <BasicModeLeft>
            {firstRow ? (
              <BasicLeftRow
                $clickable={stockEditable}
                onClick={() => {
                  if (stockEditable) openStockEdit(firstRow)
                }}
                title={stockEditable ? 'คลิกเพื่อปรับจำนวนสต็อก' : undefined}
              >
                <ThumbBox>
                  <StockImageThumb
                    src={linkedRowThumbSrc(firstRow)}
                    alt={firstRow.ref_label}
                  />
                </ThumbBox>
                <BasicLinkedMeta>
                  <Typography.Text strong>
                    {firstRow.ref_label}
                  </Typography.Text>
                  <BasicTagRow>
                    <BasicSkuTypeTag color="cyan">
                      {linkedRowTypeLabel(firstRow, skuItemId)}
                    </BasicSkuTypeTag>
                    {!isSaved && onCreateMainStock ? (
                      <Button
                        type="primary"
                        size="small"
                        icon={<PlusOutlined />}
                        loading={creatingMainStock}
                        onClick={(e) => {
                          e.stopPropagation()
                          onCreateMainStock()
                        }}
                      >
                        สร้างสต็อกหลัก
                      </Button>
                    ) : (
                      <Tag
                        color={isSaved ? 'green' : 'orange'}
                        style={{ margin: 0, fontSize: 11, lineHeight: '18px', padding: '0 8px' }}
                      >
                        {isSaved ? 'บันทึกแล้ว' : 'ยังไม่ได้บันทึก'}
                      </Tag>
                    )}
                    {stockEditable ? (
                      <EditStockHint className="edit-stock-hint">
                        <EditOutlined /> ปรับจำนวนสต็อก
                      </EditStockHint>
                    ) : null}
                  </BasicTagRow>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    ใช้{' '}
                    <Typography.Text strong>
                      {numberFormatInt(normalizeLinkedQuantity(firstRow.quantity))}
                    </Typography.Text>{' '}
                    หน่วย/ชิ้น
                  </Typography.Text>
                </BasicLinkedMeta>
              </BasicLeftRow>
            ) : null}
          </BasicModeLeft>
          <BasicModeRight>
            {onInitialQuantityChange ? (
              <BasicRightCenter>
                <div>
                  <Typography.Text strong style={{ display: 'block', marginBottom: 6 }}>
                    จำนวนสต็อกเริ่มต้น
                  </Typography.Text>
                  <InputNumber
                    min={0}
                    precision={0}
                    value={initialQuantity}
                    onChange={(v) => onInitialQuantityChange(Math.max(0, Math.floor(Number(v) || 0)))}
                    addonAfter="ชิ้น"
                    style={{ width: 180 }}
                  />
                  <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 6 }}>
                    บันทึกเป็นยอดรับเข้าคลังครั้งแรก พร้อมกับการบันทึก SKU นี้
                  </Typography.Text>
                  {initialQuantity <= 0 ? (
                    <Typography.Text type="warning" style={{ display: 'block', fontSize: 12 }}>
                      ถ้าเป็น 0 ลูกค้าจะสั่งซื้อ SKU นี้ไม่ได้จนกว่าจะเพิ่มสต็อก
                    </Typography.Text>
                  ) : null}
                </div>
              </BasicRightCenter>
            ) : showStockSummary ? (
              <BasicRightCenter>
                <StockSummaryBlock stock={stock} loading={waitingStockRefetch} />
              </BasicRightCenter>
            ) : null}
          </BasicModeRight>
        </BasicModeLayout>
      </BasicWhiteCard>
    </>
  )
}

const SOURCE_OPTIONS: { value: LinkedStockSourceKind; label: string }[] = [
  { value: 'sku', label: 'สต็อก SKU' },
  { value: 'master', label: 'มาสเตอร์' },
  { value: 'bundle', label: SET_TAG_LABEL },
]

const sourceTagColor = (kind: LinkedStockSourceKind): string => {
  if (kind === 'master') return 'blue'
  if (kind === 'bundle') return 'purple'
  return 'cyan'
}

const sourceLabel = (kind: LinkedStockSourceKind): string =>
  SOURCE_OPTIONS.find((o) => o.value === kind)?.label ?? kind

/** สต็อก SKU ของตัวเอง (sku_id ตรงกับ modal) — แสดงเป็น "หลัก" ได้แค่อันเดียว */
const isSelfPrimaryLinkedRow = (
  row: SkuLinkedStockDraftRow,
  skuItemId: string | null | undefined
): boolean =>
  row.ref_type === 'product_stock' &&
  row.source_kind === 'sku' &&
  !!skuItemId &&
  row.ref_stock_sku_id === skuItemId

const linkedRowTypeLabel = (
  row: SkuLinkedStockDraftRow,
  skuItemId: string | null | undefined
): string => {
  if (isSelfPrimaryLinkedRow(row, skuItemId)) return 'สต็อกหลัก'
  if (row.ref_stock_type_label?.trim()) return row.ref_stock_type_label.trim()
  return sourceLabel(row.source_kind)
}

/** id ของแถว picker ที่เป็น SKU ในสินค้าที่กำลังสร้าง (ยังไม่มีใน DB) — ต่อด้วย id ชั่วคราวของ SKU */
export const LOCAL_STOCK_REF_PREFIX = 'local:'
const isLocalStockRef = (id?: string | null) => !!id && id.startsWith(LOCAL_STOCK_REF_PREFIX)

/** ป้ายบอกที่มาของสต็อกใน picker — กันเลือกสต็อกของสินค้าอื่น (เช่นต้นฉบับตอนคัดลอก) โดยไม่ตั้งใจ */
const pickerSourceTag = (
  row: ProductStockPickerRow,
  productId: string | null | undefined
): { label: string; color: string } => {
  if (isLocalStockRef(row.id)) return { label: 'SKU ในสินค้านี้ (รอบันทึก)', color: 'green' }
  if (row.type === 'general') return { label: 'มาสเตอร์สต็อก', color: 'default' }
  if (productId && row.product_id === productId) return { label: 'สินค้านี้', color: 'blue' }
  return { label: row.product_name ? `สินค้าอื่น: ${row.product_name}` : 'สินค้าอื่น', color: 'orange' }
}

const resolveStockPickMeta = (
  row: ProductStockPickerRow,
  skuItemId: string | null | undefined,
  productId: string | null | undefined
): Pick<
  SkuLinkedStockDraftRow,
  'source_kind' | 'ref_stock_type_label' | 'ref_stock_sku_id'
> => {
  if (row.type === 'general') {
    return {
      source_kind: 'master',
      ref_stock_type_label: 'มาสเตอร์สต็อก',
      ref_stock_sku_id: undefined,
    }
  }
  const isSelf =
    !!skuItemId &&
    row.sku_id === skuItemId &&
    (!productId || !row.product_id || row.product_id === productId)
  const isOtherProduct = !isLocalStockRef(row.id) && (!productId || row.product_id !== productId)
  return {
    source_kind: 'sku',
    ref_stock_type_label: isSelf
      ? 'หลัก'
      : isLocalStockRef(row.id)
        ? 'SKU ในสินค้านี้'
        : isOtherProduct
          ? 'สต็อก SKU · สินค้าอื่น'
          : 'สต็อก SKU',
    ref_stock_sku_id: row.sku_id ?? undefined,
  }
}

/** ลิงก์เดียว = สต็อก SKU ของตัวเอง (ref ชี้ product_stock ที่ sku_id ตรงกับ modal) */
const isSelfSkuOnlyLinked = (
  rows: SkuLinkedStockDraftRow[],
  skuItemId: string | null | undefined
): boolean => {
  if (!skuItemId || rows.length !== 1) return false
  const row = rows[0]
  return (
    row.source_kind === 'sku' &&
    row.ref_type === 'product_stock' &&
    row.ref_stock_sku_id === skuItemId
  )
}

type SkuModalLinkedStockTabProps = {
  skuItemId?: string | null
  productId?: string | null
  skuName?: string
  skuImageUrl?: string | null
  isStock?: boolean
  stock?: IProductStockInfo | null
  isEditMode?: boolean
  /** true = SKU นี้เคยบันทึกลง DB แล้ว (ใช้ API toggle/sync ปกติ) */
  isSkuSaved?: boolean
  draftRows: SkuLinkedStockDraftRow[]
  onDraftRowsChange: (rows: SkuLinkedStockDraftRow[]) => void
  onStockToggle?: (checked: boolean) => void | Promise<void>
  onStockToggled?: () => void
  onWarehouseStockChange?: (enabled: boolean) => void
  /** เพิ่มหลัง parent sync + list แล้ว — กัน query ในแท็บทับ draft */
  linkedReloadToken?: number
  onLinkedUiModeChange?: (mode: LinkedStockUiMode) => void
  /** แจ้ง parent ว่าโหลด/ไฮเดรตข้อมูลคลังเสร็จแล้ว — ให้ปุ่มบันทึกส่ง linked_stock ได้ปลอดภัย */
  onLinkedLoaded?: (loaded: boolean) => void
  /** true = ล็อก toggle จัดการสต็อก ไม่ให้กดเปลี่ยน (ใช้ที่หน้าแก้ไขสินค้า — จัดการสต็อกผ่าน inventory grid เท่านั้น) */
  lockStockToggle?: boolean
  /** หน้าเพิ่มสินค้า: ค่าคลังที่ตั้งไว้ใน state ของหน้า (SKU ยังไม่มีใน DB) — ใช้ไฮเดรตแทน API */
  localDraft?: SkuLocalStockDraft | null
  /** หน้าเพิ่มสินค้า: SKU "จัดการสต็อกแยก" อื่นในสินค้าเดียวกัน (ยังไม่มีใน DB) ให้เลือกผูกได้ */
  localStockOptions?: ProductStockPickerRow[]
  initialQuantity?: number
  onInitialQuantityChange?: (qty: number) => void
  mainStockSaved?: boolean
  onCreateMainStock?: () => void
  creatingMainStock?: boolean
}

/** ค่าคลังของ SKU ที่ยังไม่บันทึกลง DB — rows ไม่รวมแถว "หลัก" (สร้างใหม่ทุกครั้งตอนไฮเดรต) */
export type SkuLocalStockDraft = {
  mode: LinkedStockUiMode
  rows: SkuLinkedStockDraftRow[]
}

const SkuModalLinkedStockTab: FC<SkuModalLinkedStockTabProps> = ({
  skuItemId,
  productId,
  skuName,
  skuImageUrl,
  isStock = false,
  stock,
  isEditMode = false,
  isSkuSaved = false,
  draftRows,
  onDraftRowsChange,
  onStockToggle,
  onStockToggled,
  onWarehouseStockChange,
  linkedReloadToken = 0,
  onLinkedUiModeChange,
  onLinkedLoaded,
  lockStockToggle = false,
  localDraft,
  localStockOptions,
  initialQuantity,
  onInitialQuantityChange,
  mainStockSaved,
  onCreateMainStock,
  creatingMainStock,
}) => {
  const {
    openStockEdit,
    closeStockEdit,
    editTarget,
    openIngredientPicker,
    closeIngredientPicker,
    ingredientPickerOpen,
    setPickerPanel,
  } = useSkuModalStockEdit()

  // จำนวนที่ใช้ (สต็อก) รับเฉพาะจำนวนเต็ม
  const integerGuards = createInputNumberGuards({ mode: 'integer' })

  /** แท็บ picker — "คลัง" (ยุบมาสเตอร์ + สต็อก SKU รวมกัน) / "ชุดเซ็ต" */
  const [pickerTab, setPickerTab] = useState<'combined' | 'bundle'>('combined')
  const [forceAdvanced, setForceAdvanced] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  /** หน้าปัจจุบันของตารางเลือกไอเท็ม (server-side pagination) */
  const [pickerPage, setPickerPage] = useState(1)
  const [localIsStock, setLocalIsStock] = useState(isStock)
  const [waitingStockRefetch, setWaitingStockRefetch] = useState(false)
  /** กัน auto-fill self row หลัง user ปิดสต็อกแบบพื้นฐานเอง */
  const [suppressAutoFillSelfRow, setSuppressAutoFillSelfRow] = useState(false)

  /** จดจำรายการของโหมดขั้นสูงไว้ — สลับไปพื้นฐานแล้วกลับมา ขั้นสูงจะได้ของเดิมครบ */
  const [advancedRowsMemo, setAdvancedRowsMemo] = useState<SkuLinkedStockDraftRow[] | null>(null)

  /**
   * null = ยังไม่มีข้อมูล / กำลังรอ API (แสดง skeleton)
   * 'basic' / 'advanced' = ตัดสินใจได้แล้ว (ไม่กลับ null อีก จนกว่า skuItemId เปลี่ยน หรือ toggle off→on)
   */
  const [resolvedMode, setResolvedMode] = useState<'basic' | 'advanced' | null>(null)

  /** ป้องกัน query ยิงซ้ำ — เมื่อ hydrate จาก API แล้วไม่ต้อง fetch อีก */
  const [linkedDataLoaded, setLinkedDataLoaded] = useState(false)

  /** @deprecated — เก็บไว้สำหรับ backward compat (ตอนนี้ toggle ไม่ยิง API แล้ว) */
  const pendingToggleApi = false

  const togglingStock = false

  /** โหลดสต็อก SKU ของตัวเองไว้ตั้งแต่เปิด modal (ใช้สำหรับสต็อกแบบพื้นฐาน) */
  const selfSkuStockQuery = useSkuStockPicker(
    {
      limit: 80,
      is_active: true,
      product_id: productId ?? undefined,
      sku_id: skuItemId ?? undefined,
      is_stock: true,
    },
    !!productId && !!skuItemId && isSkuSaved
  )
  const selfSkuStockRow = useMemo<ProductStockPickerRow | null>(() => {
    const list = selfSkuStockQuery.data?.data ?? []
    if (!skuItemId) return null
    // ต้องตรงทั้ง sku_id (+ product_id ถ้ามี) เท่านั้น — ถ้าไม่เจอ คืน null
    // ห้าม fallback list[0] เพราะจะได้สต็อกของ SKU อื่นมาผิด ๆ
    return (
      list.find(
        (r) =>
          r.sku_id === skuItemId &&
          (!productId || r.product_id === productId)
      ) ?? null
    )
  }, [selfSkuStockQuery.data?.data, skuItemId, productId])

  /**
   * แถว "สต็อกของตัวเอง" แสดงชื่อ/รูปตามแท็บราคาปัจจุบันเสมอ (แม้ยังไม่บันทึก)
   * ใช้เฉพาะตอนแสดงผล — draftRows ที่ส่งบันทึกไม่เปลี่ยน (อ้างแถวด้วย key)
   * skuImageUrl: undefined = ยังไม่รู้ (ใช้รูปเดิมของแถว), null = แท็บราคาไม่มีรูป
   */
  const withLiveOwnStock = useCallback(
    <T extends { ref_label: string; ref_image_id?: string | null; ref_image_url?: string | null }>(
      row: T,
      isOwn: boolean
    ): T =>
      isOwn
        ? {
            ...row,
            ref_label: liveOwnStockLabel(row.ref_label, skuName),
            ...(skuImageUrl !== undefined ? { ref_image_id: null, ref_image_url: skuImageUrl } : {}),
          }
        : row,
    [skuName, skuImageUrl]
  )
  const displayRows = useMemo(
    () =>
      draftRows.map((r) =>
        withLiveOwnStock(
          r,
          !!skuItemId && r.ref_type === 'product_stock' && r.source_kind === 'sku' && r.ref_stock_sku_id === skuItemId
        )
      ),
    [draftRows, skuItemId, withLiveOwnStock]
  )

  useEffect(() => {
    setLocalIsStock(isStock)
    setWaitingStockRefetch(false)
  }, [isStock, stock, skuItemId])

  useEffect(() => {
    onWarehouseStockChange?.(localIsStock)
  }, [localIsStock, onWarehouseStockChange])

  // แจ้ง parent ว่าข้อมูลคลังไฮเดรตเสร็จแล้ว (ปุ่มบันทึกถึงจะส่ง linked_stock ได้ปลอดภัย)
  useEffect(() => {
    onLinkedLoaded?.(linkedDataLoaded)
  }, [linkedDataLoaded, onLinkedLoaded])

  const handleStockToggle = useCallback(
    (checked: boolean) => {
      if (!skuItemId) return

      setLocalIsStock(checked)
      setResolvedMode(null)
      setLinkedDataLoaded(false)

      if (!checked) {
        onDraftRowsChange([])
      }

      const run = async () => {
        try {
          await onStockToggle?.(checked)
        } catch {
          setLocalIsStock(!checked)
          message.error('ไม่สามารถเปลี่ยนสถานะสต็อกได้')
        }
      }
      void run()

      if (checked) {
        if (isSkuSaved && selfSkuStockRow) {
          setLinkedDataLoaded(false)
        } else {
          const tempSelfRow: SkuLinkedStockDraftRow = {
            key: uuidv4(),
            source_kind: 'sku',
            ref_id: skuItemId,
            ref_type: 'product_stock',
            ref_label: skuName ?? '',
            ref_stock_type_label: 'หลัก',
            ref_image_url: skuImageUrl ?? null,
            ref_balance: 0,
            ref_stock_sku_id: skuItemId,
            quantity: 1,
          }
          onDraftRowsChange([tempSelfRow])
          setResolvedMode('basic')
          setLinkedDataLoaded(true)
        }
      }
    },
    [
      skuItemId,
      isSkuSaved,
      skuName,
      skuImageUrl,
      selfSkuStockRow,
      onStockToggle,
      onDraftRowsChange,
    ]
  )

  const linkedQuery = useListProductLinkedStock(
    { sku_id: skuItemId ?? undefined, product_id: productId ?? undefined },
    !!skuItemId && isSkuSaved && localIsStock && !linkedDataLoaded && !pendingToggleApi
  )

  // เปลี่ยน SKU (รวมตอน mount) → ล้างค่าของ SKU เดิมก่อน แล้ว effect ไฮเดรตด้านล่างค่อยเติมของ SKU นี้
  // ต้องล้างที่นี่ (ก่อน effect ไฮเดรตในคอมโพเนนต์เดียวกัน) ไม่ใช่ใน parent — effect ของ parent รันหลัง child
  // จึงจะลบค่าที่เพิ่งไฮเดรตทิ้ง
  useEffect(() => {
    onDraftRowsChange([])
    setLinkedDataLoaded(false)
    setForceAdvanced(false)
    setResolvedMode(null)
    closeIngredientPicker()
    setSuppressAutoFillSelfRow(false)
    setAdvancedRowsMemo(null)
  }, [skuItemId, closeIngredientPicker, onDraftRowsChange])

  // SKU ที่ยังไม่มีใน DB (หน้าเพิ่มสินค้า): ไม่มี API ให้โหลด → ไฮเดรตจาก localDraft ที่หน้าเก็บไว้
  useEffect(() => {
    if (isSkuSaved || !skuItemId || !localIsStock || linkedDataLoaded) return
    const mode = localDraft?.mode ?? 'basic'
    onDraftRowsChange(
      mode === 'advanced'
        ? localDraft?.rows ?? []
        : [
            {
              key: uuidv4(),
              source_kind: 'sku',
              ref_id: skuItemId,
              ref_type: 'product_stock',
              ref_label: skuName ?? '',
              ref_stock_type_label: 'หลัก',
              ref_image_url: skuImageUrl ?? null,
              ref_balance: 0,
              ref_stock_sku_id: skuItemId,
              quantity: 1,
            },
          ]
    )
    setForceAdvanced(mode === 'advanced')
    setResolvedMode(mode)
    setLinkedDataLoaded(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSkuSaved, skuItemId, localIsStock, linkedDataLoaded])

  useEffect(() => {
    if (linkedReloadToken > 0) {
      setLinkedDataLoaded(false)
      // บันทึกแล้ว → ล้าง memo ของโหมดขั้นสูง (ไม่งั้นสลับโหมดแล้วเห็นของเก่าที่ยังไม่ได้บันทึก)
      setAdvancedRowsMemo(null)
    }
  }, [linkedReloadToken])

  useEffect(() => {
    if (!skuItemId || linkedDataLoaded) return
    if (!linkedQuery.data?.data) return
    if (linkedQuery.isFetching) return

    const rows: SkuLinkedStockDraftRow[] = linkedQuery.data.data.map((r) => ({
      key: r.id,
      linked_stock_id: r.id,
      source_kind: r.source_kind,
      ref_id: r.ref_id,
      ref_type:
        r.ref_type === 'recipe' ? 'product_recipe' : 'product_stock',
      ref_label: r.ref_label,
      ref_stock_type_label:
        r.ref_stock_sku_id === skuItemId && r.source_kind === 'sku'
          ? 'หลัก'
          : r.ref_stock_type_label,
      ref_image_id: r.ref_image_id,
      ref_image_url: r.ref_image_url,
      ref_balance: r.ref_balance ?? undefined,
      ref_description: r.ref_description ?? undefined,
      ref_stock_sku_id: r.ref_stock_sku_id ?? undefined,
      quantity: normalizeLinkedQuantity(r.quantity),
    }))

    // ถ้า list ว่าง → default โหมดพื้นฐาน + เอาสต็อกตัวเองมาแสดง (edit mode)
    if (rows.length === 0) {
      if (suppressAutoFillSelfRow || !isSkuSaved) {
        onDraftRowsChange([])
      } else if (!ENABLE_ADVANCED_SELF_STOCK && forceAdvanced) {
        // โหมดขั้นสูง (flag ปิด): ไม่เติมสต็อกตัวเองเป็นรายการแรก ปล่อยตารางว่าง
        onDraftRowsChange([])
      } else if (selfSkuStockRow) {
        // มีสต็อกของตัวเองใน DB (product_id + sku_id ตรงกัน) → ใช้ของจริง
        const selfRow: SkuLinkedStockDraftRow = {
          key: uuidv4(),
          source_kind: 'sku',
          ref_id: selfSkuStockRow.id,
          ref_type: 'product_stock',
          ref_label: selfSkuStockRow.ref_label,
          ref_stock_type_label: 'หลัก',
          ref_image_id: selfSkuStockRow.images?.[0] ?? null,
          ref_image_url: skuImageUrl ?? selfSkuStockRow.image_url,
          ref_balance: selfSkuStockRow.stock_balance,
          ref_description: selfSkuStockRow.description ?? undefined,
          ref_stock_sku_id: selfSkuStockRow.sku_id ?? undefined,
          quantity: 1,
        }
        onDraftRowsChange([selfRow])
      } else if (localIsStock && skuItemId) {
        // ยังไม่เคยมีสต็อกของตัวเอง → แสดงชื่อ/รูปจาก modal (แท็บราคา) ไว้ก่อน
        // ตอนบันทึก backend จะสร้าง product_stock + self link ให้ผ่าน is_initial flag
        onDraftRowsChange([
          {
            key: uuidv4(),
            source_kind: 'sku',
            ref_id: skuItemId,
            ref_type: 'product_stock',
            ref_label: skuName ?? '',
            ref_stock_type_label: 'หลัก',
            ref_image_url: skuImageUrl ?? null,
            ref_balance: 0,
            ref_stock_sku_id: skuItemId,
            quantity: 1,
          },
        ])
      } else {
        onDraftRowsChange([])
      }
      setLinkedDataLoaded(true)
      setResolvedMode(forceAdvanced ? 'advanced' : 'basic')
      return
    }

    onDraftRowsChange(rows)
    setLinkedDataLoaded(true)

    const selfOnly = isSelfSkuOnlyLinked(rows, skuItemId)
    setResolvedMode(forceAdvanced ? 'advanced' : selfOnly ? 'basic' : 'advanced')
  }, [skuItemId, forceAdvanced, linkedDataLoaded, linkedQuery.data, linkedQuery.isFetching, onDraftRowsChange, isSkuSaved, selfSkuStockRow, suppressAutoFillSelfRow, localIsStock, skuName, skuImageUrl])

  useEffect(() => {
    if (!isSkuSaved || !selfSkuStockRow || !skuItemId) return
    if (!linkedDataLoaded) return
    if (draftRows.length > 0) return
    if (!localIsStock) return
    if (suppressAutoFillSelfRow) return
    // โหมดขั้นสูง (flag ปิด): ไม่ auto-fill สต็อกตัวเอง
    if (!ENABLE_ADVANCED_SELF_STOCK && forceAdvanced) return

    const selfRow: SkuLinkedStockDraftRow = {
      key: uuidv4(),
      source_kind: 'sku',
      ref_id: selfSkuStockRow.id,
      ref_type: 'product_stock',
      ref_label: selfSkuStockRow.ref_label,
      ref_stock_type_label: 'หลัก',
      ref_image_id: selfSkuStockRow.images?.[0] ?? null,
      ref_image_url: skuImageUrl ?? selfSkuStockRow.image_url,
      ref_balance: selfSkuStockRow.stock_balance,
      ref_description: selfSkuStockRow.description ?? undefined,
      ref_stock_sku_id: selfSkuStockRow.sku_id ?? undefined,
      quantity: 1,
    }
    onDraftRowsChange([selfRow])
    setResolvedMode(forceAdvanced ? 'advanced' : 'basic')
  }, [isSkuSaved, selfSkuStockRow, skuItemId, linkedDataLoaded, draftRows.length, localIsStock, forceAdvanced, onDraftRowsChange, suppressAutoFillSelfRow, skuImageUrl])

  useEffect(() => {
    if (draftRows.length > 1 && !forceAdvanced) {
      setForceAdvanced(true)
      setResolvedMode('advanced')
    }
  }, [draftRows.length, forceAdvanced])

  const waitingForMode = localIsStock && !!skuItemId && resolvedMode === null
  const isBasicMode = localIsStock && resolvedMode === 'basic'
  const isAdvancedMode = localIsStock && resolvedMode === 'advanced'

  useEffect(() => {
    if (!isAdvancedMode) {
      closeIngredientPicker()
    }
  }, [isAdvancedMode, closeIngredientPicker])

  useEffect(() => {
    if (!localIsStock) {
      onLinkedUiModeChange?.('basic')
      return
    }
    if (resolvedMode !== null) {
      onLinkedUiModeChange?.(resolvedMode)
    }
  }, [localIsStock, resolvedMode, onLinkedUiModeChange])

  const excludeRefIds = useMemo(
    () => draftRows.map((r) => r.ref_id),
    [draftRows]
  )

  const pickerEnabled = isAdvancedMode && ingredientPickerOpen

  /** พิมพ์แล้วค้นหาในตารางทันที (debounce) */
  useEffect(() => {
    if (!pickerEnabled) return
    const timer = window.setTimeout(() => {
      setAppliedSearch(searchInput.trim())
    }, 300)
    return () => window.clearTimeout(timer)
  }, [searchInput, pickerEnabled])

  useEffect(() => {
    if (!pickerEnabled) return
    setSearchInput('')
    setAppliedSearch('')
    setPickerPage(1)
  }, [pickerTab, pickerEnabled])

  /** เปลี่ยนคำค้น → กลับไปหน้าแรกเสมอ */
  useEffect(() => {
    setPickerPage(1)
  }, [appliedSearch])

  /** สต็อกแบบพื้นฐาน = มีลิงก์สต็อก SKU ของตัวเองอยู่ในตาราง */
  const basicSelfSkuStockEnabled = useMemo(() => {
    if (selfSkuStockRow) {
      return draftRows.some(
        (r) => r.ref_type === 'product_stock' && r.ref_id === selfSkuStockRow.id
      )
    }
    if (skuItemId) {
      return draftRows.some((r) => isSelfPrimaryLinkedRow(r, skuItemId))
    }
    return false
  }, [draftRows, selfSkuStockRow, skuItemId])

  /** ถ้ามีสต็อก SKU ของตัวเองในไอเท็ม → ดันขึ้นเป็นแถวแรกเสมอ */
  useEffect(() => {
    if (draftRows.length <= 1) return

    let idx = -1
    if (selfSkuStockRow?.id) {
      idx = draftRows.findIndex(
        (r) => r.ref_type === 'product_stock' && r.ref_id === selfSkuStockRow.id
      )
    } else if (!isSkuSaved && skuItemId) {
      idx = draftRows.findIndex(
        (r) => r.ref_type === 'product_stock' && r.ref_stock_sku_id === skuItemId
      )
    }
    if (idx <= 0) return

    const next = [draftRows[idx], ...draftRows.slice(0, idx), ...draftRows.slice(idx + 1)]
    onDraftRowsChange(next)
  }, [draftRows, selfSkuStockRow?.id, isSkuSaved, skuItemId, onDraftRowsChange])

  // แท็บ "มาสเตอร์ / สต็อก SKU": แสดงรายการทั้งหมดทันที (ไม่ต้องพิมพ์ค้นหาก่อน)
  // + แบ่งหน้าจาก server ทีละ PICKER_PAGE_SIZE (ดึงทีละหน้า)
  const combinedSearchTerm = appliedSearch.trim()
  const combinedQuery = useCombinedStockPicker(
    {
      search: combinedSearchTerm || undefined,
      page: pickerPage,
      limit: PICKER_PAGE_SIZE,
      is_active: true,
    },
    pickerEnabled && pickerTab === 'combined'
  )
  const recipeQuery = useListProductRecipes(
    { search: appliedSearch || undefined, limit: 80, is_active: true },
    pickerEnabled && pickerTab === 'bundle'
  )

  const pickerLoading =
    pickerTab === 'combined' ? combinedQuery.isFetching : recipeQuery.isFetching

  const addFromRecipe = useCallback(
    (recipe: ProductRecipeRecord) => {
      if (excludeRefIds.includes(recipe.id)) {
        message.warning('รายการนี้อยู่ในตารางแล้ว')
        return
      }
      onDraftRowsChange([
        ...draftRows,
        {
          key: uuidv4(),
          source_kind: 'bundle',
          ref_id: recipe.id,
          ref_type: 'product_recipe',
          ref_label: recipe.name,
          ref_stock_type_label: SET_TAG_LABEL,
          ref_image_id: recipe.images?.[0] ?? null,
          ref_image_url: recipe.image_url ?? null,
          ref_description: recipe.description ?? undefined,
          quantity: 1,
        },
      ])
      message.success('เพิ่มในตารางแล้ว')
    },
    [draftRows, excludeRefIds, onDraftRowsChange]
  )

  const addFromStock = useCallback(
    (row: ProductStockPickerRow) => {
      if (excludeRefIds.includes(row.id)) {
        message.warning('รายการนี้อยู่ในตารางแล้ว')
        return
      }
      const meta = resolveStockPickMeta(row, skuItemId, productId)
      onDraftRowsChange([
        ...draftRows,
        {
          key: uuidv4(),
          source_kind: meta.source_kind,
          ref_id: row.id,
          ref_type: 'product_stock',
          ref_label: row.ref_label,
          ref_stock_type_label: meta.ref_stock_type_label,
          ref_image_id: row.images?.[0] ?? null,
          ref_image_url: row.image_url,
          ref_balance: row.stock_balance,
          ref_description: row.description ?? undefined,
          ref_stock_sku_id: meta.ref_stock_sku_id,
          quantity: 1,
        },
      ])
      message.success('เพิ่มในตารางแล้ว')
    },
    [draftRows, excludeRefIds, onDraftRowsChange, skuItemId, productId]
  )

  const updateRowQuantity = useCallback(
    (key: string, value: number | null) => {
      const qty = value == null ? 0 : Number(value)
      onDraftRowsChange(
        draftRows.map((r) =>
          r.key === key
            ? { ...r, quantity: Number.isFinite(qty) ? qty : 0 }
            : r
        )
      )
    },
    [draftRows, onDraftRowsChange]
  )

  const removeRow = (key: string) => {
    const removing = draftRows.find((r) => r.key === key)
    const isSelfRow =
      (!!selfSkuStockRow?.id &&
        removing?.ref_type === 'product_stock' &&
        removing?.ref_id === selfSkuStockRow.id) ||
      (removing?.ref_type === 'product_stock' &&
        removing?.ref_stock_sku_id === skuItemId)

    if (isSelfRow) {
      // user ลบรายการ “หลัก” เอง → ปิดสต็อกแบบพื้นฐาน + กัน auto-fill กลับมาเอง
      setSuppressAutoFillSelfRow(true)
    }

    onDraftRowsChange(draftRows.filter((r) => r.key !== key))
  }

  /**
   * เปิด/ปิด "สต็อกแบบพื้นฐาน" — เปิด = ผูกสต็อก SKU ของตัวเอง (รายการ "หลัก") ไว้ในตาราง
   * ปิด = ถอดรายการ "หลัก" ออก แล้วกัน auto-fill กลับมาเอง
   */
  const handleBasicStockToggle = useCallback(
    (checked: boolean) => {
      if (!skuItemId) return
      if (checked) {
        setSuppressAutoFillSelfRow(false)
        if (selfSkuStockRow) {
          addFromStock(selfSkuStockRow)
        } else {
          const tempRow: SkuLinkedStockDraftRow = {
            key: uuidv4(),
            source_kind: 'sku',
            ref_id: skuItemId,
            ref_type: 'product_stock',
            ref_label: skuName ?? '',
            ref_stock_type_label: 'หลัก',
            ref_image_url: skuImageUrl ?? null,
            ref_balance: 0,
            ref_stock_sku_id: skuItemId,
            quantity: 1,
          }
          onDraftRowsChange([...draftRows, tempRow])
        }
      } else {
        setSuppressAutoFillSelfRow(true)
        const next = draftRows.filter((r) => {
          const matchById =
            !!selfSkuStockRow?.id &&
            r.ref_type === 'product_stock' &&
            r.ref_id === selfSkuStockRow.id
          const matchBySkuId =
            r.ref_type === 'product_stock' &&
            r.ref_stock_sku_id === skuItemId
          return !(matchById || matchBySkuId)
        })
        onDraftRowsChange(next)
      }
    },
    [
      skuItemId,
      selfSkuStockRow,
      addFromStock,
      draftRows,
      onDraftRowsChange,
      skuName,
      skuImageUrl,
    ]
  )

  /** สลับไปโหมดขั้นสูง — คืนรายการขั้นสูงที่จำไว้ (ถ้ามี) ให้ครบเหมือนเดิม */
  const handleSwitchToAdvanced = useCallback(() => {
    closeStockEdit()
    closeIngredientPicker()
    if (advancedRowsMemo && advancedRowsMemo.length > 0) {
      onDraftRowsChange(advancedRowsMemo)
    }
    setForceAdvanced(true)
    setResolvedMode('advanced')
  }, [advancedRowsMemo, closeStockEdit, closeIngredientPicker, onDraftRowsChange])

  /** สลับกลับโหมดพื้นฐาน — เหลือเฉพาะรายการ "หลัก" (สต็อก SKU ของตัวเอง) */
  const handleSwitchToBasic = useCallback(() => {
    if (!skuItemId) return
    let baseRow: SkuLinkedStockDraftRow
    if (selfSkuStockRow) {
      const existing = draftRows.find(
        (r) => r.ref_type === 'product_stock' && r.ref_id === selfSkuStockRow.id
      )
      baseRow = existing ?? {
        key: uuidv4(),
        source_kind: 'sku',
        ref_id: selfSkuStockRow.id,
        ref_type: 'product_stock',
        ref_label: selfSkuStockRow.ref_label,
        ref_stock_type_label: 'หลัก',
        ref_image_id: selfSkuStockRow.images?.[0] ?? null,
        ref_image_url: selfSkuStockRow.image_url,
        ref_balance: selfSkuStockRow.stock_balance,
        ref_description: selfSkuStockRow.description ?? undefined,
        ref_stock_sku_id: selfSkuStockRow.sku_id ?? undefined,
        quantity: 1,
      }
    } else {
      // ยังไม่มีสต็อกของตัวเอง → สร้างแถว "หลัก" ชั่วคราวจากข้อมูล SKU ใน modal
      const existing = draftRows.find(
        (r) => r.ref_type === 'product_stock' && r.ref_stock_sku_id === skuItemId
      )
      baseRow = existing ?? {
        key: uuidv4(),
        source_kind: 'sku',
        ref_id: skuItemId,
        ref_type: 'product_stock',
        ref_label: skuName ?? '',
        ref_stock_type_label: 'หลัก',
        ref_image_url: skuImageUrl ?? null,
        ref_balance: 0,
        ref_stock_sku_id: skuItemId,
        quantity: 1,
      }
    }
    setAdvancedRowsMemo(draftRows)
    onDraftRowsChange([baseRow])
    closeStockEdit()
    closeIngredientPicker()
    setSuppressAutoFillSelfRow(false)
    setForceAdvanced(false)
    setResolvedMode('basic')
  }, [
    skuItemId,
    selfSkuStockRow,
    draftRows,
    skuName,
    skuImageUrl,
    onDraftRowsChange,
    closeStockEdit,
    closeIngredientPicker,
  ])

  /** การ์ด toggle "สต็อกแบบพื้นฐาน" — ใช้ในโหมดขั้นสูง (ปิดได้ด้วย ENABLE_ADVANCED_SELF_STOCK) */
  const basicStockToggleCard = ENABLE_ADVANCED_SELF_STOCK ? (
    <BasicStockToggleCard>
      <BasicStockToggleInfo>
        <BasicStockToggleTitle>สต็อกแบบพื้นฐาน</BasicStockToggleTitle>
        <BasicStockToggleDesc>
          ใช้สต็อกของ SKU นี้โดยตรง (รายการ “หลัก”)
        </BasicStockToggleDesc>
      </BasicStockToggleInfo>
      <Switch
        checked={basicSelfSkuStockEnabled}
        onChange={handleBasicStockToggle}
        checkedChildren="เปิด"
        unCheckedChildren="ปิด"
        disabled={!skuItemId}
      />
    </BasicStockToggleCard>
  ) : null

  /** row ที่กำลังเปิด expand ถูกลบ (จากปุ่มลบ หรือ toggle ใน picker) → ปิด expand */
  useEffect(() => {
    if (!editTarget) return
    const stillExists = draftRows.some((r) => r.key === editTarget.key)
    if (!stillExists) {
      closeStockEdit()
    }
  }, [draftRows, editTarget, closeStockEdit])

  // useMemo: ingredientPickerPanel ใช้ columns เป็น dep → ถ้าสร้างใหม่ทุก render จะ setPickerPanel วนไม่รู้จบ
  const pickerStockColumns = useMemo<ColumnsType<ProductStockPickerRow>>(() => [
    {
      title: '',
      width: 48,
      render: (_, row) => (
        <ThumbBox>
          <StockImageThumb src={pickerRowThumbSrc(row)} alt={row.ref_label} />
        </ThumbBox>
      ),
    },
    {
      title: 'รายการ',
      ellipsis: true,
      render: (_, row) => {
        const tag = pickerSourceTag(row, productId)
        return (
          <>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.ref_label}</div>
            <Tag color={tag.color} style={{ margin: 0, fontSize: 11, lineHeight: '18px' }}>
              {tag.label}
            </Tag>
          </>
        )
      },
    },
    {
      title: 'คงเหลือ',
      width: 88,
      align: 'right',
      render: (_, row) => row.stock_balance.toLocaleString(),
    },
    {
      title: '',
      width: 72,
      render: (_, row) => (
        <Button
          type="link"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => addFromStock(row)}
        >
          เพิ่ม
        </Button>
      ),
    },
  ], [addFromStock, productId])

  const pickerRecipeColumns = useMemo<ColumnsType<ProductRecipeRecord>>(() => [
    {
      title: 'ชื่อชุดเซ็ต',
      dataIndex: 'name',
      ellipsis: true,
    },
    {
      title: 'ส่วนประกอบ',
      width: 100,
      render: (_, row) => row.item_count ?? 0,
    },
    {
      title: '',
      width: 72,
      render: (_, row) => (
        <Button
          type="link"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => addFromRecipe(row)}
        >
          เพิ่ม
        </Button>
      ),
    },
  ], [addFromRecipe])

  const stockPickerRows = useMemo(() => {
    const term = combinedSearchTerm?.trim().toLowerCase()
    // SKU ในสินค้านี้ที่ยังไม่บันทึก ขึ้นก่อนเฉพาะหน้าแรก (ไม่ได้มาจาก server)
    const local =
      pickerPage === 1
        ? (localStockOptions ?? []).filter((r) => !term || r.ref_label.toLowerCase().includes(term))
        : []
    const isOwnSkuStock = (r: ProductStockPickerRow) =>
      r.type === 'sku' && !!skuItemId && r.sku_id === skuItemId
    const list = [...local, ...(combinedQuery.data?.data ?? [])]
    return (
      list
        // ตัดรายการที่อยู่ในตารางไอเท็มแล้วออก (กันเลือกซ้ำ)
        .filter((r) => !excludeRefIds.includes(r.id))
        // สต็อกของ SKU ตัวเอง: โหมดขั้นสูงไม่ใช้ (ENABLE_ADVANCED_SELF_STOCK ปิด → ถูกซ่อน/ตัดตอนบันทึก)
        // ถ้าให้เลือกได้ กด "เพิ่ม" แล้วแถวจะหายไปเฉย ๆ
        .filter((r) => ENABLE_ADVANCED_SELF_STOCK || !isOwnSkuStock(r))
        // เปิด flag: ชื่อ/รูปตามแท็บราคาปัจจุบัน
        .map((r) =>
          isOwnSkuStock(r)
            ? {
                ...r,
                ref_label: liveOwnStockLabel(r.ref_label, skuName),
                ...(skuImageUrl !== undefined ? { images: null, image_url: skuImageUrl } : {}),
              }
            : r
        )
    )
  }, [combinedQuery.data?.data, excludeRefIds, localStockOptions, combinedSearchTerm, pickerPage, skuItemId, skuName, skuImageUrl])

  /** จำนวนรายการทั้งหมดจาก server — ใช้คุม pagination แบบ server-side */
  const stockPickerTotal = combinedQuery.data?.total ?? 0

  const recipePickerRows = useMemo(() => {
    const list = recipeQuery.data?.data ?? []
    return list.filter((r) => !excludeRefIds.includes(r.id))
  }, [recipeQuery.data?.data, excludeRefIds])

  /**
   * สรุปสต็อกโหมดพื้นฐาน — อ่านจาก "สต็อก SKU ของตัวเอง" จริง (selfSkuStockRow)
   * เพราะ prop `stock` เป็น "สต็อกที่ขายได้" (sellable view) ที่ backend คืน holding_quantity = 0 เสมอ
   * สำหรับ SKU ที่มี self-link (ทุกตัวในโหมดพื้นฐาน) → "กำลังมีคนซื้อ" เลยไม่เคยขึ้นค่า
   * selfSkuStockRow มาจาก listSkuStockPicker ซึ่งคืน quantity/holding_quantity/stock_balance ของ product_stock จริง
   * SKU ที่ตอนนี้ "เชื่อมกับสต็อกหลัก" → sku/list ไม่คืนสต็อกของตัวเอง (ตัดเฉพาะ SKU พื้นฐาน)
   * สลับมาโหมดพื้นฐานแล้วจึงถาม product_stock เดิมตรง ๆ (ownStockQuery) · ไม่เคยมี = 0 0 0
   * fallback กลับไป prop stock เฉพาะตอนถาม API ไม่ได้ (SKU ยังไม่บันทึกลง DB)
   */
  const ownStockQuery = useSkuOwnStock(
    { product_id: productId ?? '', sku_id: skuItemId ?? '' },
    isSkuSaved && isBasicMode && selfSkuStockQuery.isFetched && !selfSkuStockRow
  )
  const basicSummaryStock = useMemo<IProductStockInfo | null>(() => {
    if (selfSkuStockRow) {
      return {
        id: selfSkuStockRow.id,
        stock_quantity: selfSkuStockRow.quantity,
        holding_quantity: selfSkuStockRow.holding_quantity,
        stock_balance: selfSkuStockRow.stock_balance,
      }
    }
    const own = ownStockQuery.data?.data
    if (own) {
      return {
        id: own.id,
        stock_quantity: own.quantity,
        holding_quantity: own.holding_quantity,
        stock_balance: own.quantity - own.holding_quantity,
      }
    }
    // prop stock = ยอดขายได้ของโหมดเดิม (คำนวณจากของที่ผูกไว้) ไม่ใช่สต็อกของ SKU เอง
    if (ownStockQuery.isSuccess) return { stock_quantity: 0, holding_quantity: 0, stock_balance: 0 }
    return stock ?? null
  }, [selfSkuStockRow, ownStockQuery.data, ownStockQuery.isSuccess, stock])

  const basicSummaryLoading = waitingStockRefetch || ownStockQuery.isLoading
  const showStockSummaryBasic =
    localIsStock && isBasicMode && !!(basicSummaryLoading || basicSummaryStock)

  /** canMake = floor(ref_balance / quantity) — ใช้คำนวณพร้อมขายรวม */
  /** โหมดขั้นสูง (ENABLE_ADVANCED_SELF_STOCK ปิด): ไม่นำสต็อก SKU ของตัวเอง (รายการ "หลัก") มาแสดงในไอเท็มที่ผูกไว้ */
  const ingredientReadyList = useMemo(
    () =>
      displayRows
        .filter(
          (r) => ENABLE_ADVANCED_SELF_STOCK || !isSelfPrimaryLinkedRow(r, skuItemId)
        )
        .map((r) => {
          const qty = normalizeLinkedQuantity(r.quantity)
          const balance = r.ref_balance ?? null
          const canMake =
            balance != null && qty > 0
              ? Math.floor(balance / qty)
              : null
          return { ...r, canMake }
        }),
    [displayRows, skuItemId]
  )

  const handleIngredientRowClick = useCallback(
    (row: SkuLinkedStockDraftRow) => {
      if (!canEditLinkedStockRow(row, skuItemId)) return
      openStockEdit(row)
    },
    [openStockEdit, skuItemId]
  )

  const handleCloseIngredientPicker = useCallback(() => {
    closeIngredientPicker()
    setSearchInput('')
    setAppliedSearch('')
  }, [closeIngredientPicker])

  const ingredientPickerPanel = useMemo(() => {
    if (!ingredientPickerOpen || !isAdvancedMode) return null

    return (
      <PickerSlideRoot>
        <PickerCard>
          <PickerBar>
            <PickerBarTop>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                เพิ่มรายการจาก
              </Typography.Text>
              <Button
                type="link"
                size="small"
                style={{ padding: 0, marginLeft: 'auto' }}
                onClick={handleCloseIngredientPicker}
              >
                ปิด
              </Button>
            </PickerBarTop>
            <PickerControlRow>
              <PickerKindSegmented>
                <Segmented
                  size="small"
                  block
                  value={pickerTab}
                  onChange={(v) => setPickerTab(v as 'combined' | 'bundle')}
                  options={[
                    { value: 'combined', label: 'มาสเตอร์' },
                    { value: 'bundle', label: SET_TAG_LABEL, disabled: true },
                  ]}
                />
              </PickerKindSegmented>

              <PickerSearchWrap>
                <Input
                  size="small"
                  placeholder={
                    pickerTab === 'combined'
                      ? 'ค้นหามาสเตอร์ / สต็อก SKU…'
                      : `ค้นหา${SET_TAG_LABEL}…`
                  }
                  value={searchInput}
                  allowClear
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </PickerSearchWrap>
            </PickerControlRow>
          </PickerBar>

          <PickerResultSection>
            <PickerTableWrap>
              {pickerTab === 'bundle' ? (
                <Table<ProductRecipeRecord>
                  size="small"
                  rowKey="id"
                  loading={pickerLoading}
                  columns={pickerRecipeColumns}
                  dataSource={recipePickerRows}
                  pagination={{
                    pageSize: 10,
                    size: 'small',
                    showSizeChanger: false,
                  }}
                  scroll={{ y: 'min(55vh, 520px)' }}
                />
              ) : (
                <Table<ProductStockPickerRow>
                  size="small"
                  rowKey="id"
                  loading={pickerLoading}
                  columns={pickerStockColumns}
                  dataSource={stockPickerRows}
                  pagination={false}
                  scroll={{ y: 'min(55vh, 520px)' }}
                  locale={{ emptyText: 'ไม่พบรายการ' }}
                />
              )}
            </PickerTableWrap>
            {pickerTab === 'combined' && stockPickerTotal > PICKER_PAGE_SIZE ? (
              <PickerPaginationRow>
                <Pagination
                  size="small"
                  current={pickerPage}
                  pageSize={PICKER_PAGE_SIZE}
                  total={stockPickerTotal}
                  showSizeChanger={false}
                  onChange={(p) => setPickerPage(p)}
                />
              </PickerPaginationRow>
            ) : null}
          </PickerResultSection>
        </PickerCard>
      </PickerSlideRoot>
    )
  }, [
    ingredientPickerOpen,
    isAdvancedMode,
    handleCloseIngredientPicker,
    pickerTab,
    searchInput,
    pickerLoading,
    pickerRecipeColumns,
    recipePickerRows,
    pickerStockColumns,
    stockPickerRows,
    stockPickerTotal,
    pickerPage,
  ])

  useEffect(() => {
    setPickerPanel(ingredientPickerPanel)
    return () => setPickerPanel(null)
  }, [ingredientPickerPanel, setPickerPanel])

  useEffect(
    () => () => {
      closeIngredientPicker()
    },
    [closeIngredientPicker]
  )

  return (
    <Wrap>
      <StockToolbar>
        <StockToolbarLabel>สต็อกของตัวเลือก:</StockToolbarLabel>
        {skuName ? (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {skuName}
          </Typography.Text>
        ) : null}
        <StockToolbarRight>
          {togglingStock ? (
            <Skeleton.Button active size="small" shape="round" style={{ width: 64, height: 22 }} />
          ) : (
            <Tooltip
              title={
                lockStockToggle
                  ? 'จัดการสต็อกได้ที่หน้าคลังสินค้าเท่านั้น'
                  : !skuItemId
                    ? 'บันทึก SKU ก่อนจึงจะจัดการสต็อกได้'
                    : localIsStock
                      ? 'คลิกเพื่อตั้งเป็นไม่จำกัดสต็อก'
                      : 'คลิกเพื่อจำกัดจำนวนสต็อก'
              }
            >
              <Switch
                checked={localIsStock}
                onChange={handleStockToggle}
                checkedChildren="เปิดติดตามสต็อก"
                unCheckedChildren="ไม่จำกัด"
                disabled={lockStockToggle || !skuItemId || (!productId && isEditMode)}
              />
            </Tooltip>
          )}
        </StockToolbarRight>
      </StockToolbar>

      {localIsStock ? (
        <>
          {waitingForMode ? (
            <ModeSkeleton>
              <Skeleton.Button active size="small" shape="round" style={{ width: 80, height: 22 }} />
              <Skeleton.Input active size="small" style={{ width: '55%', height: 22, marginTop: 12 }} />
              <Skeleton active paragraph={{ rows: 2, width: ['80%', '60%'] }} title={false} style={{ marginTop: 8 }} />
              <BasicLinkedCardSkeleton>
                <Skeleton.Image active style={{ width: 52, height: 52, borderRadius: 6 }} />
                <SkeletonMetaWrap>
                  <Skeleton.Input active size="small" style={{ width: 160, height: 18 }} />
                  <Skeleton.Button active size="small" shape="round" style={{ width: 64, height: 20, marginTop: 4 }} />
                  <Skeleton.Input active size="small" style={{ width: 100, height: 16, marginTop: 4 }} />
                </SkeletonMetaWrap>
              </BasicLinkedCardSkeleton>
            </ModeSkeleton>
          ) : isBasicMode ? (
            <BasicModeView
              draftRows={displayRows}
              stock={basicSummaryStock}
              showStockSummary={showStockSummaryBasic}
              waitingStockRefetch={basicSummaryLoading}
              skuItemId={skuItemId}
              onSwitchToAdvanced={handleSwitchToAdvanced}
              initialQuantity={initialQuantity}
              onInitialQuantityChange={onInitialQuantityChange}
              mainStockSaved={mainStockSaved}
              onCreateMainStock={onCreateMainStock}
              creatingMainStock={creatingMainStock}
            />
          ) : isAdvancedMode ? (
            <>
              {linkedQuery.isFetching && skuItemId ? (
                <Spin style={{ display: 'block', margin: '12px auto' }} />
              ) : null}

              <AdvancedModeHeader>
                <ModeDesc>{STOCK_MODE_ADVANCED_DESC}</ModeDesc>
                <StockModeToggle
                  mode="advanced"
                  disabled={!skuItemId}
                  onChange={(m) => {
                    if (m === 'basic') handleSwitchToBasic()
                  }}
                />
              </AdvancedModeHeader>

              {basicStockToggleCard}

              <Layout>
            <LeftColumn $full>
              <IngredientSummaryCard>
                <IngredientCardHeader>
                  <IngredientCardTitleRow>
                    <IngredientCardTitle>ไอเท็มที่ผูกไว้</IngredientCardTitle>
                    <SaveStatusLegend>
                      <SaveStatusLegendItem>
                        <SaveStatusDot $variant="saved" />
                        <span>บันทึกแล้ว</span>
                      </SaveStatusLegendItem>
                      <SaveStatusLegendItem>
                        <SaveStatusDot $variant="draft" />
                        <span>ยังไม่ได้บันทึก</span>
                      </SaveStatusLegendItem>
                    </SaveStatusLegend>
                  </IngredientCardTitleRow>
                  {ingredientReadyList.length > 0 && ingredientReadyList.every((r) => r.canMake != null) ? (
                    <IngredientTotalReady>
                      <IngredientReadyLabel>ขายได้</IngredientReadyLabel>
                      <IngredientReadyValue
                        $positive={Math.min(...ingredientReadyList.map((r) => r.canMake ?? 0)) > 0}
                      >
                        {Math.min(...ingredientReadyList.map((r) => r.canMake ?? 0)).toLocaleString()}{' '}
                        <span style={{ fontSize: 11, fontWeight: 400, color: '#8c8c8c' }}>ชิ้น</span>
                      </IngredientReadyValue>
                    </IngredientTotalReady>
                  ) : null}
                </IngredientCardHeader>

                <IngredientItemsList>
                  {ingredientReadyList.length === 0 ? (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="กรุณาเลือกไอเท็มที่ต้องการผูกกับ SKU นี้"
                    />
                  ) : (
                    ingredientReadyList.map((item) => {
                      const stockEditable = canEditLinkedStockRow(item, skuItemId)
                      return (
                      <IngredientItem
                        key={item.key}
                        $clickable={stockEditable}
                        onClick={() => handleIngredientRowClick(item)}
                        title={stockEditable ? 'คลิกเพื่อปรับจำนวนสต็อก' : undefined}
                      >
                        <ThumbBox>
                          <StockImageThumb src={linkedRowThumbSrc(item)} alt={item.ref_label} />
                        </ThumbBox>
                        <IngredientMeta>
                          <IngredientName title={item.ref_label}>
                            {item.ref_label}
                          </IngredientName>
                          <IngredientTagRow>
                            <BasicSkuTypeTag color={sourceTagColor(item.source_kind)}>
                              {linkedRowTypeLabel(item, skuItemId)}
                            </BasicSkuTypeTag>
                            {item.linked_stock_id ? (
                              <SaveStatusTag color="success">บันทึกแล้ว</SaveStatusTag>
                            ) : (
                              <SaveStatusTag color="warning">ยังไม่ได้บันทึก</SaveStatusTag>
                            )}
                            {stockEditable ? (
                              <EditStockHint className="edit-stock-hint">
                        <EditOutlined /> ปรับจำนวนสต็อก
                      </EditStockHint>
                            ) : null}
                          </IngredientTagRow>
                        </IngredientMeta>
                        <IngredientQtyCol onClick={(e) => e.stopPropagation()}>
                          <IngredientReadyLabel>จำนวนที่ใช้</IngredientReadyLabel>
                          {item.ref_stock_type_label === 'หลัก' ? (
                            // ประเภท "หลัก" (สต็อกตัวเอง) แก้ไขจำนวนที่ใช้ไม่ได้ → แสดงเป็น label
                            <IngredientQtyText>
                              {numberFormatInt(normalizeLinkedQuantity(item.quantity))}
                            </IngredientQtyText>
                          ) : (
                            <InputNumber
                              min={0}
                              precision={0}
                              step={1}
                              size="small"
                              style={{ width: 80 }}
                              // ลบจนว่าง → quantity เป็น 0 → แสดงช่องว่าง (ไม่ใช่ "0" ที่พิมพ์ต่อแล้วกลายเป็น "03")
                              // 0 ใช้ไม่ได้อยู่แล้ว — ตอนบันทึกบังคับให้มากกว่า 0
                              value={item.quantity || null}
                              onChange={(v) =>
                                updateRowQuantity(item.key, v == null ? null : Number(v))
                              }
                              {...integerGuards}
                            />
                          )}
                        </IngredientQtyCol>
                        <IngredientReady>
                          <IngredientReadyLabel>คงเหลือ</IngredientReadyLabel>
                          <IngredientReadyValue
                            $positive={item.ref_balance != null && item.ref_balance > 0}
                          >
                            {item.ref_balance != null
                              ? numberFormatInt(item.ref_balance)
                              : '—'}
                          </IngredientReadyValue>
                        </IngredientReady>
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={(e) => {
                            e.stopPropagation()
                            removeRow(item.key)
                          }}
                        />
                      </IngredientItem>
                    )})
                  )}
                  {!ingredientPickerOpen ? (
                    <IngredientAddRow
                      type="button"
                      onClick={openIngredientPicker}
                      $pulse={ingredientReadyList.length === 0}
                    >
                      <span className="add-icon">
                        <PlusOutlined />
                      </span>
                      <span className="add-label">เชื่อมสต็อกหลัก</span>
                    </IngredientAddRow>
                  ) : null}
                </IngredientItemsList>
              </IngredientSummaryCard>
            </LeftColumn>
          </Layout>
          </>
          ) : null}
        </>
      ) : (
        <LinkedStockHint>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="เปิดจัดการสต็อก (จำกัด) เพื่อผูกรายการคลังกับ SKU นี้"
          />
        </LinkedStockHint>
      )}
    </Wrap>
  )
}

export default SkuModalLinkedStockTab

const Wrap = styled.div`
  padding: 4px 0 8px;
  width: 100%;
  max-width: ${SKU_MODAL_WAREHOUSE_WIDTH}px;
  min-width: 0;
  box-sizing: border-box;
  overflow-x: hidden;
`

const StockToolbar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 16px;
  margin-bottom: 12px;
  padding: 10px 12px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
`

const StockToolbarLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #434343;
`

const StockToolbarRight = styled.div`
  margin-left: auto;
`

const StockSummaryRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
`

const StockSummary = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;
  margin-bottom: 12px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
`

const StockStat = styled.div<{ $emphasis?: boolean }>`
  flex: ${(p) => (p.$emphasis ? 1.22 : 1)};
  min-width: 0;
  min-height: 46px;
  display: grid;
  grid-template-rows: 1fr auto;
  align-items: end;
  justify-items: center;
  padding: 5px 8px 6px;
  background: ${(p) => (p.$emphasis ? '#fffbf5' : 'transparent')};
`

const StockStatLabel = styled.div`
  font-size: 10px;
  color: #8c8c8c;
  line-height: 1.25;
  text-align: center;
  white-space: nowrap;
`

const StockStatValue = styled.div<{
  $tone: 'default' | 'hold' | 'balance'
  $positive?: boolean
}>`
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
  color: ${(p) => {
    if (p.$tone === 'hold') return '#d46b08'
    if (p.$tone === 'balance') return p.$positive ? '#389e0d' : '#cf1322'
    return '#434343'
  }};
`

const StockStatDivider = styled.div`
  width: 1px;
  align-self: stretch;
  background: #e8e8e8;
  flex-shrink: 0;
`

const Layout = styled.div`
  display: flex;
  width: 100%;
`

const LeftColumn = styled.div<{ $full?: boolean }>`
  flex: 1 1 auto;
  width: 100%;
  max-width: 100%;
  min-width: 0;
`

const PickerSlideRoot = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 320px;
  max-height: min(80vh, 720px);
`

const PickerCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  max-height: inherit;
  padding: 10px 12px;
  overflow: hidden;
`

const PickerResultSection = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  margin-top: 8px;
  overflow: hidden;
`

const PickerPaginationRow = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
  padding: 8px 2px 2px;
`



const PickerBar = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const PickerBarTop = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 10px;
`

const PickerControlRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`

/** การ์ด toggle "สต็อกแบบพื้นฐาน" — เด่นชัด อยู่เหนือการ์ดไอเท็มที่ผูกไว้ในโหมดขั้นสูง */
const BasicStockToggleCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  padding: 10px 14px;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 8px;
`

const BasicStockToggleInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const BasicStockToggleTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #237804;
`

const BasicStockToggleDesc = styled.span`
  font-size: 11px;
  color: #8c8c8c;
  line-height: 1.3;
`

const PickerKindSegmented = styled.div`
  width: 100%;

  .ant-segmented {
    width: 100%;
  }
  .ant-segmented-item {
    flex: 1;
    min-width: 0;
  }
  .ant-segmented-item-label {
    font-size: 12px;
    padding: 0 6px;
  }
`

/** ช่องค้นหา — ไม่ override ความสูงของ ant-input (กันเส้นขอบหาย) */
const PickerSearchWrap = styled.div`
  flex: 1;
  min-width: 0;
  /* เผื่อพื้นที่ให้ focus ring ของ ant-input ไม่โดนตัดใน card */
  padding: 1px;
  margin: -1px;

  .ant-input-affix-wrapper {
    width: 100%;
  }
`

/** ตารางผลการค้นหา — ตัวอักษรเล็กลง + scroll ภายใน */
const PickerTableWrap = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;

  .ant-table {
    font-size: 12px;
  }
  /* คงความสูง body ไว้คงที่ (เท่า scroll.y) แม้มีไม่กี่แถว — กัน pagination ขยับขึ้น-ลง */
  .ant-table-body {
    min-height: min(55vh, 520px);
  }
  /* เมื่อไม่มีข้อมูล ก็ยังคงความสูงเท่าเดิม */
  .ant-table-placeholder .ant-table-cell {
    height: min(55vh, 520px);
  }
  .ant-table-thead > tr > th {
    font-size: 11px;
    padding: 6px 8px;
  }
  .ant-table-tbody > tr > td {
    font-size: 12px;
    padding: 6px 8px;
  }
  .ant-table-pagination {
    font-size: 12px;
    margin: 6px 0 0;
  }
  .ant-pagination {
    margin: 0;
  }
  .ant-pagination-item,
  .ant-pagination-prev,
  .ant-pagination-next {
    min-width: 22px;
    height: 22px;
    line-height: 20px;
    margin-inline-end: 4px;
  }
  .ant-pagination-item a {
    padding: 0 6px;
    line-height: 20px;
  }
  .ant-pagination-prev .ant-pagination-item-link,
  .ant-pagination-next .ant-pagination-item-link {
    width: 22px;
    height: 22px;
  }
  .ant-pagination-prev,
  .ant-pagination-next {
    margin-inline-end: 4px;
  }
  .ant-btn-link {
    font-size: 12px;
    height: auto;
    padding: 0 4px;
  }
`

const ThumbBox = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 6px;
  overflow: hidden;
`

const LinkedStockHint = styled.div`
  margin-top: 8px;
  padding: 24px 16px;
  background: #fafafa;
  border: 1px dashed #e8e8e8;
  border-radius: 8px;
`

const ModeSkeleton = styled.div`
  margin-top: 8px;
  padding: 16px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
`

const BasicLinkedCardSkeleton = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 12px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  margin-top: 12px;
`

const SkeletonMetaWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`


/** คำอธิบายโหมดที่เลือกอยู่ — ชิดซ้ายในแถวเดียวกับ toggle (ชื่อโหมดอ่านได้จาก toggle อยู่แล้ว) */
const ModeDesc = styled.span`
  flex: 1 1 240px;
  min-width: 0;
  font-size: 11px;
  line-height: 1.4;
  color: #8c8c8c;
`

/** wrapper ของ toggle โหมด — ทำสีตัวที่ selected ตามโหมด (กำหนดโดยตรง = ฟ้า, คำนวณ = ม่วง) */
const ModeToggleWrap = styled.div<{ $variant: 'basic' | 'advanced' }>`
  .ant-segmented-item-selected,
  .ant-segmented-thumb {
    background-color: ${(p) => (p.$variant === 'basic' ? '#1677ff' : '#722ed1')};
  }
  .ant-segmented-item-selected,
  .ant-segmented-item-selected:hover,
  .ant-segmented-item-selected .ant-segmented-item-label {
    color: #ffffff;
  }
`

const BasicModeHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 12px;
  margin-bottom: 8px;
`


const BasicWhiteCard = styled.div<{ $variant: 'split' }>`
  width: 100%;
  padding: 12px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;

  /* ให้ StockSummary ไม่ชนขอบตอนจอแคบ */
  & > div {
    margin-bottom: 0;
  }
`

/** แถวบนสุดในการ์ดพื้นฐาน — โชว์คำอธิบายสถานะบันทึก (ชิดขวา) */
const BasicCardLegendRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
`

const BasicLinkedMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const BasicSkuTypeTag = styled(Tag)`
  margin-top: 4px;
  width: fit-content;
  align-self: flex-start;
  margin-inline-end: 0;
  font-size: 11px;
  line-height: 18px;
  padding: 0 6px;
`


const BasicModeLayout = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: stretch;
  margin-top: 10px;
`

const BasicModeLeft = styled.div`
  flex: 1 1 320px;
  min-width: 0;
  display: flex;
`

const BasicModeRight = styled.div`
  flex: 1 1 260px;
  min-width: 0;
  display: flex;
  align-items: stretch;
`

const BasicLeftRow = styled.div<{ $clickable?: boolean }>`
  width: 100%;
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: flex-start;
  padding: 6px 8px;
  margin: -6px -8px;
  border-radius: 6px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: background 0.15s ease;

  ${({ $clickable }) =>
    $clickable
      ? `
    &:hover {
      background: #f5f9ff;
    }
    &:hover .edit-stock-hint {
      background: #1677ff;
      border-color: #1677ff;
      color: #fff;
    }
  `
      : ''}
`

const BasicTagRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`

const BasicRightCenter = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 1024px) {
    justify-content: flex-start;
  }
`

const AdvancedModeHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 12px;
  margin: 8px 0;
`

const IngredientSummaryCard = styled.div`
  width: 100%;
  padding: 10px 12px;
  margin-bottom: 12px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 0;
`

const IngredientCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 8px;
  margin-bottom: 4px;
  border-bottom: 1px solid #f0f0f0;
`

const IngredientCardTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #595959;
`

const IngredientCardTitleRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`

const SaveStatusLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
`

const SaveStatusLegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: #8c8c8c;
`

const SaveStatusDot = styled.span<{ $variant: 'saved' | 'draft' }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(p) => (p.$variant === 'saved' ? '#52c41a' : '#faad14')};
`

const SaveStatusTag = styled(Tag)`
  margin: 0 !important;
  font-size: 10px !important;
  line-height: 16px !important;
  padding: 0 4px !important;
`

const IngredientTotalReady = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1px;
`

const IngredientItemsList = styled.div`
  max-height: 480px;
  overflow-y: auto;
  margin: 0 -12px;
  padding: 0 12px;
`

const IngredientItem = styled.div<{ $clickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 8px;
  margin: 0 -8px;
  border-bottom: 1px solid #f5f5f5;
  border-radius: 6px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: background 0.15s ease;

  ${({ $clickable }) =>
    $clickable
      ? `
    &:hover {
      background: #f5f9ff;
    }
    &:hover .edit-stock-hint {
      background: #1677ff;
      border-color: #1677ff;
      color: #fff;
    }
  `
      : ''}
`

/** ป้าย "แก้ไขสต็อก" — ทำเป็นปุ่มให้เห็นชัดว่ากดได้ (ทั้งแถวเป็นตัวกด) */
const EditStockHint = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 2px;
  padding: 0 8px;
  font-size: 11px;
  font-weight: 600;
  line-height: 20px;
  color: #1677ff;
  background: #e6f4ff;
  border: 1px solid #91caff;
  border-radius: 10px;
  white-space: nowrap;
  transition: all 0.15s ease;

  .anticon {
    font-size: 11px;
  }
`

const IngredientAddRow = styled.button<{ $pulse?: boolean }>`
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  margin-top: 4px;
  padding: 10px 12px;
  border: 1px solid #fa8c16;
  border-radius: 6px;
  background: #fa8c16;
  cursor: pointer;
  animation: ingredientAddFadeIn 0.24s ease;
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;

  @keyframes ingredientAddFadeIn {
    from {
      opacity: 0;
      transform: translateY(4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* เอฟเฟค "เงาดาบคาตานะ" — แสงวาบเฉียงพาดผ่านปุ่มแวบนึง เป็นระยะ เมื่อตารางว่าง */
  @keyframes katanaShine {
    0%,
    72% {
      transform: translateX(-160%) skewX(-20deg);
    }
    82%,
    100% {
      transform: translateX(260%) skewX(-20deg);
    }
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 36%;
    height: 100%;
    background: linear-gradient(
      100deg,
      transparent 0%,
      rgba(255, 255, 255, 0) 30%,
      rgba(255, 255, 255, 0.85) 50%,
      rgba(255, 255, 255, 0) 70%,
      transparent 100%
    );
    transform: translateX(-160%) skewX(-20deg);
    pointer-events: none;
    opacity: ${({ $pulse }) => ($pulse ? 1 : 0)};
    animation: ${({ $pulse }) =>
      $pulse ? 'katanaShine 4.2s ease-in-out infinite' : 'none'};
  }

  & .add-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #fff;
    border: 1px solid #fff;
    font-size: 12px;
    line-height: 1;
    color: #fa8c16;
    transition: border-color 0.2s, color 0.2s;
  }

  & .add-label {
    font-size: 13px;
    font-weight: 600;
    color: #fff;
    transition: color 0.2s;
  }

  ${({ $pulse }) =>
    $pulse
      ? `box-shadow: 0 0 0 0 rgba(250, 140, 22, 0.45);
         animation: ingredientAddFadeIn 0.24s ease, addRowGlow 2.6s ease-in-out infinite;`
      : ''}

  @keyframes addRowGlow {
    0%,
    100% {
      box-shadow: 0 0 0 0 rgba(250, 140, 22, 0);
    }
    50% {
      box-shadow: 0 0 8px 1px rgba(250, 140, 22, 0.35);
    }
  }

  &:hover {
    border-color: #d46b08;
    background: #d46b08;
  }

  &:hover .add-icon {
    border-color: #fff;
    color: #d46b08;
  }

  &:hover .add-label {
    color: #fff;
  }

  &:active {
    background: #ad4e00;
  }
`

const IngredientMeta = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const IngredientName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #262626;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const IngredientTagRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`

const IngredientQtyCol = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
  min-width: 72px;
`

const IngredientReady = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1px;
  min-width: 64px;
`

const IngredientReadyLabel = styled.div`
  font-size: 10px;
  color: #8c8c8c;
  line-height: 1.2;
`

const IngredientReadyValue = styled.div<{ $positive?: boolean }>`
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${(p) => (p.$positive ? '#389e0d' : '#cf1322')};
`

/** จำนวนที่ใช้แบบ label (ประเภท "หลัก" แก้ไขไม่ได้) */
const IngredientQtyText = styled.div`
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #434343;
  line-height: 1.2;
`
